import { Injectable } from '@angular/core';
import { StoreName } from '../entities/StoreName';
import { DataService } from './data.service';
import { UserService } from './user.service';
import { GlobalConstants } from '../constant/constant';
import FormDefinition from '../models/form-definition/form-definition';
import { FormDefinitionSyncResponse } from '../models/form-definition/form-definition-sync-response';
import { DbTransaction } from '../models/db/db-transaction';
import { EventBusService } from './event-bus.service';
import { AppEventType } from '../enums/app-events.enum';
import { ProjectService } from './project.service';
import { DateTimeHelper } from '../utils/date-time-helper';

@Injectable({
  providedIn: 'root',
})
export class FormDefinitionsService extends DataService {
  constructor(private userService: UserService, private projectService: ProjectService, private eventBusService: EventBusService) {
    super();
  }

  async getAll(filter?: any, existingTransaction?: DbTransaction): Promise<FormDefinition[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    return await this.repository.getAll(StoreName.FormDefinition, filter, undefined, transaction);
  }

  async getAllByProject(projectId: number): Promise<FormDefinition[]> {
    return await this.repository.getAllByIndex(StoreName.FormDefinition, GlobalConstants.KEY_NAMES.ProjectId, projectId);
  }

  async getOne(formDefinitionId: number, existingTransaction?: DbTransaction): Promise<FormDefinition> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    return await this.repository.getOneByIndex(StoreName.FormDefinition, GlobalConstants.KEY_NAMES.Id, formDefinitionId, undefined, transaction);
  }

  async getOneByBaseDefId(baseDefId: number): Promise<FormDefinition> {
    return await this.repository.getOneByIndex(StoreName.FormDefinition, GlobalConstants.KEY_NAMES.FormDefinitionBaseId, baseDefId);
  }

  async getMany(formDefinitionIds: number[], existingTransaction?: DbTransaction): Promise<FormDefinition[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    return await this.repository.getAllByIndex(StoreName.FormDefinition, GlobalConstants.KEY_NAMES.Id, formDefinitionIds, undefined, transaction);
  }

  private async insertOne(form: FormDefinition, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const existingForm = await this.getOne(form.id, transaction);
    if (existingForm) {
      form.RecordId = existingForm.RecordId;
    }

    await this.repository.create(form, StoreName.FormDefinition, transaction);
  }

  private async insertMany(forms: FormDefinition[], existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    for (const form of forms) {
      await this.insertOne(form, transaction);
    }
  }

  private async deleteMany(forms: FormDefinition[]) {
    await this.repository.deleteBulkData(forms, StoreName.FormDefinition);
  }

  private async deleteAll() {
    await this.repository.deleteAll(StoreName.FormDefinition);
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.FormDefinition], 'readwrite'));
  }

  async sync() {
    const currentForms = await this.getAll();

    let validatedForms: { id: number; version: number }[] = [];
    if (currentForms.length === 0) {
      await this.deleteAll();
    } else {
      validatedForms.push(...currentForms.filter(form => form.version));
    }

    const currentUser = await this.userService.getCurrentUser();
    const projectIds = await this.projectService.getActiveAssignedProjectIds(currentUser.Id, DateTimeHelper.getLocalNowString());

    const data = await this.http
      .post<FormDefinitionSyncResponse>(this.FLEX_FORMS_API_ENDPOINTS.SyncForms, {
        ProjectIds: projectIds,
        Forms: validatedForms,
      })
      .toPromise();

    if (data.forms.length) {
      const mappedData = data.forms
        .filter(form => form.action !== 'DELETE')
        .filter(form => form.formDefinition.baseFormDefinitionId !== undefined && form.formDefinition.version !== undefined)
        .map(form => ({
          id: form.id,
          description: form.description,
          title: form.title,
          fieldActivityIds: form.fieldActivityIds ?? [],
          version: form.version,
          ...form.formDefinition,
        }));

      const inactivatedForms = data.forms.filter(form => form.action === 'DELETE');

      const inactivatedFormDefinitions = await this.getMany(inactivatedForms.map(f => f.id));

      await this.deleteMany(inactivatedFormDefinitions);

      await this.insertMany(mappedData);
    }

    this.eventBusService.emit({ type: AppEventType.FORM_DEFINITIONS_SYNC_COMPLETED, data: null });
  }

  checkIsKioskForm(formDefinition: FormDefinition): boolean {
    return !!formDefinition.kioskSettings;
  }

  /**
   * Returns true when the provided form definition should show an ending QR code
   * (either shared form QR code or receipt QR code).
   */
  checkHasEndingQrCode(formDefinition?: FormDefinition): boolean {
    if (!formDefinition) return false;
    return !!(formDefinition.isShareable || formDefinition.shareableProducerId || formDefinition.receipt);
  }
}
